import { isEqual } from 'lodash-es';
import { useEffect, useMemo, useRef } from 'react';
import { shallow } from 'zustand/shallow';

import { useToolPlayControllerContext } from '@/component/Tool/ToolPlayController';
import { useWatch } from '@/package';
import {
  usePcdCacheStore,
  usePcdFileStore,
  usePcdRenderingStore,
  useTaskFrameStore,
} from '@/store';
import type { FrameData } from '@/type';

const useFramePcdFileList = ({
  frameSeq,
  frameList,
}: {
  frameSeq: FrameData['frameSeq'];
  frameList: FrameData[];
}) => {
  const pcdIndex = usePcdRenderingStore(state => state.pcdIndex);
  const currentFrame = useTaskFrameStore(state => state.currentFrame);
  const cacheStatus = usePcdCacheStore(state => state.cacheStatus);
  const path = usePcdFileStore(state => state.path);
  const { isPlaying } = useToolPlayControllerContext();
  const prevFrameSeqRef = useRef(frameSeq);
  const cacheStatusPath = cacheStatus[path];

  const pcdNameEntry = useMemo(() => {
    const pcdNameEntry = [] as [number, string][];

    frameList.forEach(frame => {
      const pcdIndex = frame.frameSeq - 1;
      const pcdFileName = frame.filename;
      const pcdData = usePcdRenderingStore.getState().getPcdData(pcdIndex);
      const isLoading = cacheStatusPath?.[pcdFileName]?.isLoading;

      if (pcdData || isLoading) return;

      usePcdCacheStore
        .getState()
        .addPcdPathList(path, [[pcdIndex, pcdFileName]]);

      pcdNameEntry.push([pcdIndex, pcdFileName]);
    });

    return pcdNameEntry;
  }, [cacheStatusPath, frameList, path]);

  useEffect(() => {
    if (isPlaying) return;

    const prevFrameSeq = prevFrameSeqRef.current;
    const frameSeqDiff = Math.abs(frameSeq - prevFrameSeq);

    if (frameSeqDiff > 10) usePcdFileStore.getState().clearFetchPcdQueue();

    prevFrameSeqRef.current = frameSeq;
  }, [frameSeq, isPlaying]);

  useWatch(
    currentFrame,
    currentFrame => {
      if (
        !currentFrame ||
        !currentFrame.filename ||
        usePcdRenderingStore.getState().getPcdData(currentFrame.frameSeq - 1)
      )
        return;

      usePcdFileStore
        .getState()
        .fetchPcd(currentFrame.filename)
        .then(pcdData => {
          usePcdRenderingStore
            .getState()
            .setPcdData(currentFrame.frameSeq - 1, pcdData);
        });
    },
    {
      equalityFn: shallow,
    },
  );

  useWatch(
    [currentFrame, frameSeq, pcdIndex, pcdNameEntry] as const,
    ([, , , newFileEntryList]) => {
      if (
        !useTaskFrameStore.getState().isValidFrameDetail() ||
        !newFileEntryList.length
      )
        return;

      const isInEntry =
        pcdNameEntry.findIndex(([index]) => frameSeq - 1 === index) >= 0;

      usePcdCacheStore
        .getState()
        .addPcdPathList(path, pcdNameEntry)
        .then(() => {
          isInEntry && useTaskFrameStore.getState().setIsFrameFileLoading(true);

          if (pcdNameEntry.length > 1) {
            return usePcdFileStore
              .getState()
              .fetchPcdList(pcdNameEntry.map(v => v[1]))
              .then(valueList =>
                valueList.map((value, i) => {
                  usePcdRenderingStore
                    .getState()
                    .setPcdData(pcdNameEntry[i][0], value);
                }),
              )
              .catch(error => {
                console.group('[useFramePcdFile] fetchPcdList Error');
                console.warn(error);
                console.groupEnd();
              });
          } else {
            return usePcdFileStore
              .getState()
              .fetchPcd(pcdNameEntry[0][1])
              .then(value => {
                usePcdRenderingStore
                  .getState()
                  .setPcdData(pcdNameEntry[0][0], value);
              })
              .catch(error => {
                console.group('[useFramePcdFile] fetchPcd Error');
                console.warn(error);
                console.groupEnd();
              });
          }
        })
        .finally(() => {
          isInEntry &&
            useTaskFrameStore.getState().setIsFrameFileLoading(false);
        });
    },
    {
      equalityFn([, , , newFileEntryList], [, , , oldFileEntryList]) {
        return isEqual(newFileEntryList, oldFileEntryList);
      },
    },
  );
};

export default useFramePcdFileList;
